/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import { useAuth } from '../../context/AuthContext';
import FirebaseStorageService from '../../services/firebase-storage.service';
import OnboardingLayout, { CancelButton } from './OnboardingLayout';
import './onboarding.css';
import { v4 as uuidv4 } from 'uuid';

const ProfileDocuments: React.FC = () => {
  const navigate = useNavigate();
  const { companyData, updateCompanyData, setCurrentStep } = useOnboarding();
  const { currentUser, signInAnonymously } = useAuth();

  // Efecto para autenticar anónimamente si no hay usuario
  useEffect(() => {
    const ensureAuthentication = async () => {
      if (!currentUser) {
        try {
          await signInAnonymously();
        } catch (error) {
          console.error('Error al crear usuario anónimo:', error);
        }
      }
    };

    ensureAuthentication();
  }, [currentUser, signInAnonymously]);

  // Estado para la imagen de perfil (puede ser una URL o un objeto con preview y URL)
  const [profileImage, setProfileImage] = useState<string | { preview: string; url?: string; uploaded?: boolean } | null>(
    companyData.profileImage || null
  );

  // Estado para rastrear si la imagen de perfil está en proceso de carga
  const [isProfileImageUploading, setIsProfileImageUploading] = useState<boolean>(false);

  // Estados para manejar la imagen de perfil y documentos
  const [documents, setDocuments] = useState<{
    id: string;
    name: string;
    file: File;
    type: string;
    size: number;
    uploaded: boolean;
    category?: string; // Categoría del documento (cédula, cámara de comercio, RUT)
    preview?: string; // Vista previa para cuando es una imagen
    url?: string; // URL de Firebase Storage
  }[]>([]);

  // Estado para manejar imágenes adicionales
  const [additionalImages, setAdditionalImages] = useState<{
    id: string;
    name: string;
    file: File;
    preview: string;
    size: number;
    url?: string; // URL de Firebase Storage
    uploaded?: boolean; // Indica si la imagen se ha subido correctamente
  }[]>([]);

  // Documentos requeridos
  const requiredDocuments = [
    { id: 'cedula', name: 'Cédula del Representante Legal', type: 'PDF' },
    { id: 'camara', name: 'Cámara de Comercio', type: 'XLS' },
    { id: 'rut', name: 'RUT', type: 'XLS' }
  ];

  // Verificar si hay una categoría seleccionada
  useEffect(() => {
    if (!companyData.category) {
      navigate('/onboarding/category');
    }
  }, [companyData, navigate]);

  // Verificar si hay una imagen guardada en localStorage al cargar la página
  useEffect(() => {
    const savedProfileImage = localStorage.getItem('userProfileImage');

    if (savedProfileImage) {
      setProfileImage(savedProfileImage);
      // Actualizar también el contexto
      updateCompanyData({ profileImage: savedProfileImage });
    }
  }, []);

  // Inicializar la imagen de perfil con la imagen de Google del usuario si está disponible
  // y solo si no hay una imagen ya subida por el usuario
  useEffect(() => {
    // Verificar si hay una imagen guardada en localStorage
    const savedProfileImage = localStorage.getItem('userProfileImage');

    // Si hay una imagen en localStorage, usarla con prioridad
    if (savedProfileImage) {
      if (profileImage !== savedProfileImage) {
        setProfileImage(savedProfileImage);
        updateCompanyData({ profileImage: savedProfileImage });
      }
      return;
    }

    // Verificar si el usuario tiene una foto de perfil de Google
    if (currentUser) {
      // Solo actualizar si no hay una imagen ya establecida y el usuario tiene una foto
      // Verificamos también que no haya una imagen en el contexto
      if (currentUser.photoURL && !profileImage && !companyData.profileImage) {
        setProfileImage(currentUser.photoURL);
      }
    }
  }, [currentUser, companyData.profileImage]); // Añadimos companyData.profileImage para detectar cambios en el contexto

  // Manejar la selección de imagen de perfil
  const handleProfileImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Activar el estado de carga
      setIsProfileImageUploading(true);

      // Verificar si hay un usuario autenticado, si no, crear uno anónimo
      if (!currentUser) {
        try {
          await signInAnonymously();
        } catch (error) {
          setIsProfileImageUploading(false); // Desactivar estado de carga si hay error
          return; // Salir si no se pudo crear un usuario anónimo
        }
      }

      // Crear una vista previa local para mostrar inmediatamente
      const reader = new FileReader();
      reader.onload = async (event) => {
        if (event.target?.result) {
          // Guardar la vista previa local como objeto con preview y marcar como no subida aún
          const previewStr = event.target.result as string;
          setProfileImage({ preview: previewStr, uploaded: false });

          try {
            // Subir la imagen a Firebase Storage
            const url = await FirebaseStorageService.uploadFile(file, 'profile-images');

            // Actualizar el estado con la URL y marcar como subida correctamente
            setProfileImage({ preview: previewStr, url, uploaded: true });

            // Actualizar inmediatamente en el contexto para asegurar que se envíe al backend
            // y sobrescribir cualquier URL de Google que pudiera estar presente
            updateCompanyData({ profileImage: url });

            // Guardar también en localStorage como respaldo
            localStorage.setItem('userProfileImage', url);

            // Forzar la actualización del contexto para asegurar que se use esta URL
            setTimeout(() => {
              // Verificar que la URL en el contexto sea la correcta
              if (companyData.profileImage !== url) {
                updateCompanyData({ profileImage: url });
              }
            }, 500);
          } catch (error) {
            console.error('Error al subir imagen de perfil a Firebase Storage:', error);
            alert('Error al subir la imagen de perfil. Por favor, intenta nuevamente.');
          } finally {
            setIsProfileImageUploading(false); // Desactivar estado de carga
          }
        }
      };

      reader.readAsDataURL(file);
    }
  };

  // Manejar la subida de documentos
  const handleDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>, category: string) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];

      // Verificar si hay un usuario autenticado, si no, crear uno anónimo
      if (!currentUser) {
        try {
          await signInAnonymously();
        } catch (error) {
          return; // Salir si no se pudo crear un usuario anónimo
        }
      }

      const docId = uuidv4(); // Usar uuid para generar IDs consistentes

      // Crear el nuevo documento con el tipo correcto
      const newFile: {
        id: string;
        name: string;
        file: File;
        type: string;
        size: number;
        uploaded: boolean;
        category?: string;
        preview?: string;
        url?: string; // Añadimos la URL para almacenar la referencia a Firebase Storage
      } = {
        id: docId,
        file: file,
        name: file.name,
        type: file.type.split('/')[1]?.toUpperCase() || 'DOC',
        size: file.size / (1024 * 1024), // Convertir a MB
        uploaded: false,
        category: category,

      };

      try {
        // Verificar si ya existe un documento con esta categoría
        const existingIndex = documents.findIndex(doc => doc.category === category);
        let updatedDocuments = [...documents];

        if (existingIndex !== -1) {
          // Reemplazar el documento existente
          updatedDocuments.splice(existingIndex, 1);
        }

        // Mostrar un documento temporal mientras se sube
        setDocuments([...updatedDocuments, newFile]);

        // Subir el archivo a Firebase Storage usando nuestro servicio
        // El servicio ahora maneja automáticamente la estructura de carpetas basada en el usuario
        const url = await FirebaseStorageService.uploadFile(file, `documents/${category}`);

        // Actualizar el documento con la URL de Firebase
        newFile.url = url;
        newFile.uploaded = true;

        // Si es una imagen, generar una vista previa
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target?.result) {
              newFile.preview = e.target.result as string;
              // Actualizar el estado con el documento actualizado
              setDocuments([...updatedDocuments, newFile]);
            }
          };
          reader.readAsDataURL(file);
        } else {
          // Para documentos no imagen, solo actualizamos con la URL
          setDocuments([...updatedDocuments, newFile]);
        }
      } catch (error) {
        // Marcar el documento como no subido en caso de error
        newFile.uploaded = false;

        // Verificar si ya existe un documento con esta categoría
        const existingIndex = documents.findIndex(doc => doc.category === category);
        let updatedDocs = [...documents];

        if (existingIndex !== -1) {
          // Reemplazar el documento existente
          updatedDocs.splice(existingIndex, 1);
        }

        setDocuments([...updatedDocs, newFile]);
      }
    }
  };

  // Eliminar un documento
  const handleRemoveDocument = (id: string) => {
    setDocuments(prev => prev.filter(doc => doc.id !== id));
  };

  // Verificar si un documento de cierta categoría ya ha sido subido
  const isDocumentUploaded = (categoryId: string) => {
    return documents.some(doc => doc.category === categoryId);
  };

  // Manejar la subida de imágenes adicionales
  const handleAdditionalImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.type.startsWith('image/')) {
        // Verificar si hay un usuario autenticado, si no, crear uno anónimo
        if (!currentUser) {
          try {
            await signInAnonymously();
          } catch (error) {
            return; // Salir si no se pudo crear un usuario anónimo
          }
        }

        // Generar un ID único para la imagen
        const imageId = uuidv4();

        // Crear un objeto temporal para la imagen con vista previa local
        const reader = new FileReader();
        reader.onload = async (event) => {
          if (event.target?.result) {
            const newImage = {
              id: imageId,
              name: file.name,
              file: file,
              preview: event.target.result as string,
              size: file.size / (1024 * 1024), // Convertir a MB
              url: undefined, // Inicialmente undefined, se actualizará cuando se suba
              uploaded: false
            };

            // Agregar la imagen temporal al estado
            setAdditionalImages(prev => [...prev, newImage]);

            try {
              // Subir la imagen a Firebase Storage
              const url = await FirebaseStorageService.uploadFile(file, 'additional-images');

              // Actualizar el estado con la URL de la imagen
              setAdditionalImages(prev =>
                prev.map(img =>
                  img.id === imageId ? { ...img, url, uploaded: true } : img
                )
              );
            } catch (error) {
              setAdditionalImages(prev =>
                prev.map(img =>
                  img.id === imageId ? { ...img, uploaded: false } : img
                )
              );
            }
          }
        };

        reader.readAsDataURL(file);
      }
    }
  };

  // Eliminar una imagen adicional
  /* const handleRemoveAdditionalImage = (id: string) => {
    setAdditionalImages(prev => prev.filter(img => img.id !== id));
  }; */

  const handleBack = () => {
    setCurrentStep(2);
    navigate('/onboarding/services');
  };

  // Manejar el botón de continuar
  const handleContinue = () => {
    // Verificar que se hayan subido todos los documentos requeridos
    const allDocumentsUploaded = requiredDocuments.every(reqDoc =>
      isDocumentUploaded(reqDoc.id)
    );

    // Verificar si la imagen de perfil está en proceso de carga
    if (isProfileImageUploading) {
      alert('Por favor, espera a que la imagen de perfil termine de subirse.');
      return;
    }

    // Verificar si la imagen de perfil es un objeto y necesita estar subida
    const isProfileImageUploaded = typeof profileImage === 'object' ?
      (profileImage?.uploaded && !!profileImage?.url) : true;

    if (!isProfileImageUploaded) {
      alert('Por favor, espera a que la imagen de perfil se suba correctamente antes de continuar.');
      return;
    }

    if (allDocumentsUploaded) {
      const allUploaded = documents.every(doc => doc.uploaded && doc.url);

      if (!allUploaded) {
        alert('Por favor, espera a que todos los documentos se suban correctamente antes de continuar.');
        return;
      }

      // Preparar la URL de la imagen de perfil para enviar al backend
      let finalProfileImageUrl = null;

      // Si es un objeto con URL de Firebase, usar esa URL
      if (typeof profileImage === 'object' && profileImage && profileImage.url) {
        finalProfileImageUrl = profileImage.url;
      }
      // Si es una string y NO es una URL de Google (que comienza con https://lh3.googleusercontent.com),
      // entonces usarla
      else if (typeof profileImage === 'string' && !profileImage.includes('googleusercontent.com')) {
        finalProfileImageUrl = profileImage;
      }
      // Si no hay imagen de Firebase pero hay una URL de Google, usarla como último recurso
      else if (typeof profileImage === 'string') {
        finalProfileImageUrl = profileImage;
      }

      // Guardar los documentos en el contexto (solo URLs, no archivos completos)
      updateCompanyData({
        // Establecer explícitamente la URL de la imagen de perfil
        profileImage: finalProfileImageUrl,
        documents: documents.map(doc => ({
          name: doc.name,
          type: doc.type,
          size: doc.size,
          category: doc.category,
          // Ya no enviamos el archivo completo
          url: doc.url || '', // Usar la URL de Firebase Storage
          // Mantener preview solo para visualización local
          preview: doc.preview
        })),
        additionalImages: additionalImages.map(img => ({
          name: img.name,
          // Mantener preview para visualización local
          preview: img.preview,
          size: img.size,
          url: img.url || '' // Usar la URL de Firebase Storage
        }))
      });

      // Guardar la URL de la imagen de perfil en localStorage como respaldo
      if (finalProfileImageUrl) {
        localStorage.setItem('userProfileImage', finalProfileImageUrl);
      }

      setCurrentStep(4); // Avanzar al siguiente paso
      navigate('/onboarding/company-details'); // Navegar a la siguiente página
    }
  };

  // Formatear el tamaño del archivo
  const formatFileSize = (size: number) => {
    return size.toFixed(1) + ' MB';
  };

  return (
    <OnboardingLayout
      title="¡Tu identidad, tu respaldo!"
      subtitle="Sube la foto de perfil de tu empresa y los documentos requeridos para validar tu información. Esto nos ayuda a construir un entorno seguro y confiable."
      showBackButton={true}
      onBack={handleBack}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

        {/* Sección de documentos */}
        <div className="flex flex-col">
          <h3 className="font-medium text-xervix-text-primary dark:text-xervix-text-primary-dark mb-2">
            Documentos
          </h3>
          <div className="bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark rounded-lg p-4 h-auto overflow-y-auto">
            <div className="space-y-2">
              {/* Lista de documentos requeridos con su estado */}
              <ul className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-sm space-y-4 ">
                {requiredDocuments.map(reqDoc => {
                  const isUploaded = isDocumentUploaded(reqDoc.id);
                  const uploadedDoc = documents.find(doc => doc.category === reqDoc.id);

                  return (
                    <li key={reqDoc.id} className="flex flex-col">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {/* Icono del tipo de archivo - solo si está subido */}
                          {isUploaded && uploadedDoc && (
                            <div className={`w-8 h-6 rounded flex items-center justify-center flex-shrink-0 ${uploadedDoc.type === 'PDF' ? 'bg-red-100 text-red-500' :
                              uploadedDoc.type === 'XLS' || uploadedDoc.type === 'XLSX' ? 'bg-green-100 text-green-500' :
                                uploadedDoc.type === 'JPG' || uploadedDoc.type === 'JPEG' || uploadedDoc.type === 'PNG' || uploadedDoc.type === 'WEBP' ? 'bg-blue-100 text-blue-500' :
                                  'bg-gray-100 text-gray-500'
                              }`}>
                              <span className="text-[10px] font-bold">{uploadedDoc.type}</span>
                            </div>
                          )}
                          <span className={isUploaded ? "" : "ml-2"}>{reqDoc.name}</span>
                        </div>

                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${isUploaded
                          ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300'
                          : 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-300'
                          }`}>
                          {isUploaded ? 'Subido' : 'Pendiente'}
                        </span>
                      </div>

                      {/* Si el documento está subido, mostrar detalles del archivo */}
                      {isUploaded && uploadedDoc && (
                        <div className="mt-1 flex justify-between items-center">
                          <p className="text-xs text-xervix-text-secondary dark:text-xervix-text-secondary-dark truncate max-w-[200px]">
                            {uploadedDoc.name}
                          </p>
                          <p className="text-xs text-xervix-text-secondary dark:text-xervix-text-secondary-dark ml-2">
                            ({formatFileSize(uploadedDoc.size)})
                          </p>
                        </div>
                      )}

                      {/* Botón para subir/reemplazar documento */}
                      <div className="">
                        <label className="cursor-pointer text-xs text-xervix-primary hover:text-opacity-80 transition-colors">
                          <span>{isUploaded ? 'Reemplazar documento' : 'Subir documento'}</span>
                          <input
                            type="file"
                            accept={
                              reqDoc.type === 'PDF'
                                ? 'application/pdf,.pdf,image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp'
                                : reqDoc.type === 'XLS'
                                  ? 'application/pdf,.pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xls,.xlsx,image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp'
                                  : '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                            }
                            onChange={(e) => handleDocumentUpload(e, reqDoc.id)}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {/* Sección de imagen de perfil */}
        <div className="flex flex-col items-center">
          <div className="relative w-32 h-32 rounded-full bg-xervix-bg-tertiary dark:bg-xervix-bg-tertiary-dark flex items-center justify-center overflow-hidden mb-4">
            {/* Debug info - usamos un efecto para evitar errores de lint */}
            <div style={{ display: 'none' }}>
              {(() => {
                return null;
              })()}
            </div>

            {/* Mostrar la imagen de perfil con indicador de carga */}
            {isProfileImageUploading ? (
              <div className="flex items-center justify-center w-full h-full bg-black bg-opacity-50">
                <div className="loader w-8 h-8 border-2 border-t-2 border-white rounded-full animate-spin"></div>
              </div>
            ) : profileImage ? (
              <div className="relative w-full h-full">
                <img
                  src={typeof profileImage === 'string' ? profileImage : (profileImage.url || profileImage.preview)}
                  alt="Perfil"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://via.placeholder.com/150';
                  }}
                />
                {typeof profileImage === 'object' && !profileImage.uploaded && profileImage.preview && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                    <div className="loader w-8 h-8 border-2 border-t-2 border-white rounded-full animate-spin"></div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-full bg-gray-200 text-gray-500">
                <span>Sin foto</span>
              </div>
            )}
          </div>
          {!profileImage && (
            <p className="text-xervix-text-secondary dark:text-xervix-text-secondary-dark text-center mb-4">
              No deseas usar la imagen de Google?
            </p>
          )}
          <label className="cursor-pointer text-xervix-primary hover:text-opacity-80 transition-colors">
            <span>Sube tu imagen aquí</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleProfileImageChange}
              className="hidden"
            />
          </label>

          {/* Botones de acción */}
          <div className="flex justify-center gap-4 mt-8">
            <CancelButton />
            <button
              onClick={handleContinue}
              disabled={documents.length < 3}
              className={`px-8 py-3 rounded-md text-white font-medium transition-colors ${documents.length < 3 ? 'bg-gray-400 cursor-not-allowed' : 'bg-xervix-primary hover:bg-opacity-90'}`}
            >
              Continuar
            </button>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default ProfileDocuments;
